import { SETORES, UNIDADES } from "@caint/data";
import type { ChaveSelo, Eixo, Momento } from "@caint/engine";
import type { ItemMerged } from "../state/useAppState.ts";
import { META_SUBINDICE, porSetor } from "./analiseRelatorio.ts";
import type { AvaliacaoRemota } from "./api.ts";
import { engine } from "./engine.ts";

/**
 * Panorama da rede: agregação COMPARATIVA entre unidades (visão de gestão). Puro
 * — recebe as entradas já montadas (uma por unidade: a avaliação do ciclo mais
 * recente + os itens recortados pelo escopo) e devolve o ranking por unidade e os
 * cruzamentos de rede (sistêmico × isolado, dispersão, comparabilidade por NA%).
 *
 * A montagem das entradas (resolver escopo + `itensDaAvaliacao`) fica no hook, que
 * já detém esse padrão em `evolucao` — mantém esta lib sem IO e testável, e evita
 * ciclo de import com `useAppState`. O IMGQ é RECALCULADO aqui (o tipo cliente
 * `AvaliacaoRemota` não traz os caches do servidor), igual ao painel de evolução.
 */

/** Uma unidade + os itens do seu ciclo mais recente, já recortados pelo escopo. */
export interface EntradaUnidade {
  readonly av: AvaliacaoRemota;
  readonly itens: readonly ItemMerged[];
  /** Momento avaliado (o app opera em "mi"). Default "mi". */
  readonly momento?: Momento;
}

export interface SetorFraco {
  readonly code: string;
  readonly nome: string;
  readonly score: number | null;
}

/** Linha do ranking: uma unidade e seu diagnóstico de IMGQ. */
export interface UnidadePanorama {
  readonly unidadeId: string;
  readonly unidadeNome: string;
  readonly avaliacaoId: string;
  readonly cicloId: string;
  readonly momento: Momento;
  /** IMGQ do checklist (efetivo, pré-monitoramento) — base comparável da rede. */
  readonly imgq: number;
  readonly bruto: number;
  readonly selo: ChaveSelo;
  readonly seloRotulo: string;
  readonly travado: boolean;
  readonly motivosTrava: readonly string[];
  readonly subs: Record<Eixo, number | null>;
  /** Universo aplicável (itens em setores ativos) e quantos já avaliados. */
  readonly nItens: number;
  readonly nAvaliados: number;
  /** % de itens NA sobre os aplicáveis — variável de comparabilidade (parecer IMGQ). */
  readonly naPct: number;
  readonly coreCriticos: number;
  readonly setoresFracos: readonly SetorFraco[];
  readonly atualizadoEm: string;
}

export interface SetorRede {
  readonly code: string;
  readonly nome: string;
  readonly unidades: readonly string[];
}
export interface ItemCoreRede {
  readonly itemId: string;
  readonly code: string;
  readonly pergunta: string;
  readonly unidades: readonly string[];
}
export interface FatiaSelo {
  readonly chave: ChaveSelo;
  readonly rotulo: string;
  readonly n: number;
}

/** Cruzamentos entre unidades: o que é sistêmico, o que é isolado, dispersão. */
export interface CruzamentoRede {
  /** Setor fraco (< meta) em ≥ 2 unidades → problema sistêmico da rede. */
  readonly setoresSistemicos: readonly SetorRede[];
  /** Setor fraco em exatamente 1 unidade → problema isolado/local. */
  readonly setoresIsolados: readonly SetorRede[];
  /** Item CORE em nível 0/1 recorrente (≥ 2 unidades) — lacuna a corrigir na rede. */
  readonly itensCoreRecorrentes: readonly ItemCoreRede[];
  readonly imgqMedio: number;
  readonly imgqMin: number;
  readonly imgqMax: number;
  readonly imgqDesvio: number;
  readonly naMin: number;
  readonly naMax: number;
  /** Amplitude de NA% entre unidades, em pontos percentuais. */
  readonly naSpread: number;
  /** NA% divergente > 15pp compromete a comparação direta (parecer técnico IMGQ). */
  readonly naAlerta: boolean;
  readonly nTravadas: number;
  readonly distSelos: readonly FatiaSelo[];
}

export interface Panorama {
  /** Unidades ordenadas por IMGQ (ranking, maior → menor). */
  readonly unidades: readonly UnidadePanorama[];
  readonly rede: CruzamentoRede;
  readonly nUnidades: number;
  readonly nCiclos: number;
}

const NOME_EIXO: Record<Eixo, string> = {
  IMGO: "Gestão Operacional",
  IMQA: "Qualidade Assistencial",
  IMGR: "Gestão de Riscos",
};
const EIXOS: readonly Eixo[] = ["IMGO", "IMQA", "IMGR"];
const nomeSetor = (code: string): string => SETORES.find((s) => s.code === code)?.nome ?? code;
const nomeUnidade = (id: string): string => UNIDADES.find((u) => u.id === id)?.nome ?? id;

/** Diagnóstico de uma unidade a partir dos seus itens (recortados pelo escopo). */
function diagnosticar(e: EntradaUnidade): UnidadePanorama {
  const momento = e.momento ?? "mi";
  const its = e.itens;
  const r = engine.imgq(its, momento);
  const s = engine.selo(r.efetivo);
  const na = engine.contarNA(its, momento);
  const nItens = its.length;
  const nAvaliados = its.filter((it) => it[momento] != null).length;
  const subs: Record<Eixo, number | null> = { IMGO: r.imgo, IMQA: r.imqa, IMGR: r.imgr };

  const motivos: string[] = [];
  if (r.core0 + r.core1 > 0) {
    motivos.push(`${r.core0 + r.core1} item(ns) CORE em nível 0/1`);
  }
  for (const eixo of EIXOS) {
    const v = subs[eixo];
    if (v != null && v < META_SUBINDICE) {
      motivos.push(`${NOME_EIXO[eixo]} < ${Math.round(META_SUBINDICE * 100)}%`);
    }
  }

  const setoresFracos: SetorFraco[] = porSetor(its, momento)
    .filter((x) => x.score != null && x.score < META_SUBINDICE)
    .map((x) => ({ code: x.code, nome: x.nome, score: x.score }));

  return {
    unidadeId: e.av.unidadeId,
    unidadeNome: nomeUnidade(e.av.unidadeId),
    avaliacaoId: e.av.id,
    cicloId: e.av.cicloId,
    momento,
    imgq: r.efetivo,
    bruto: r.bruto,
    selo: s.chave,
    seloRotulo: s.rotulo,
    travado: r.travado,
    motivosTrava: motivos,
    subs,
    nItens,
    nAvaliados,
    naPct: nItens > 0 ? na / nItens : 0,
    coreCriticos: r.core0 + r.core1,
    setoresFracos,
    atualizadoEm: e.av.atualizadoEm,
  };
}

/** Desvio-padrão populacional de uma amostra (0 se ≤ 1 elemento). */
function desvio(xs: readonly number[]): number {
  if (xs.length <= 1) return 0;
  const m = xs.reduce((a, b) => a + b, 0) / xs.length;
  const v = xs.reduce((a, b) => a + (b - m) ** 2, 0) / xs.length;
  return Math.sqrt(v);
}

/** Cruza as unidades: setores sistêmicos × isolados, CORE recorrentes, dispersão. */
function cruzar(us: readonly UnidadePanorama[], entradas: readonly EntradaUnidade[]): CruzamentoRede {
  // Setores fracos por unidade → conta em quantas unidades cada setor é fraco.
  const setorUnidades = new Map<string, Set<string>>();
  for (const u of us) {
    for (const sf of u.setoresFracos) {
      const set = setorUnidades.get(sf.code) ?? new Set<string>();
      set.add(u.unidadeNome);
      setorUnidades.set(sf.code, set);
    }
  }
  const setoresSistemicos: SetorRede[] = [];
  const setoresIsolados: SetorRede[] = [];
  for (const [code, uset] of setorUnidades) {
    const linha = { code, nome: nomeSetor(code), unidades: [...uset] };
    (uset.size >= 2 ? setoresSistemicos : setoresIsolados).push(linha);
  }
  setoresSistemicos.sort((a, b) => b.unidades.length - a.unidades.length);

  // Itens CORE (peso 3) em nível 0/1 → conta em quantas unidades cada item recorre.
  const coreUnidades = new Map<string, { pergunta: string; code: string; uset: Set<string> }>();
  for (const e of entradas) {
    const nome = nomeUnidade(e.av.unidadeId);
    const momento = e.momento ?? "mi";
    for (const it of e.itens) {
      const v = it[momento];
      if (it.peso === 3 && (v === "0" || v === "1")) {
        const cur = coreUnidades.get(it.id) ?? { pergunta: it.pergunta, code: it.code, uset: new Set() };
        cur.uset.add(nome);
        coreUnidades.set(it.id, cur);
      }
    }
  }
  const itensCoreRecorrentes: ItemCoreRede[] = [...coreUnidades.entries()]
    .filter(([, v]) => v.uset.size >= 2)
    .map(([itemId, v]) => ({ itemId, code: v.code, pergunta: v.pergunta, unidades: [...v.uset] }))
    .sort((a, b) => b.unidades.length - a.unidades.length);

  const imgqs = us.map((u) => u.imgq);
  const nas = us.map((u) => u.naPct * 100);
  const naMin = nas.length ? Math.min(...nas) : 0;
  const naMax = nas.length ? Math.max(...nas) : 0;

  // Distribuição de selos (ordem: ouro, prata, bronze, dev, pend).
  const ordemSelo: ChaveSelo[] = ["ouro", "prata", "bronze", "dev", "pend"];
  const contagem = new Map<ChaveSelo, { rotulo: string; n: number }>();
  for (const u of us) {
    const cur = contagem.get(u.selo) ?? { rotulo: u.seloRotulo, n: 0 };
    cur.n += 1;
    contagem.set(u.selo, cur);
  }
  const distSelos: FatiaSelo[] = ordemSelo
    .filter((c) => contagem.has(c))
    .map((c) => ({ chave: c, rotulo: contagem.get(c)?.rotulo ?? c, n: contagem.get(c)?.n ?? 0 }));

  return {
    setoresSistemicos,
    setoresIsolados,
    itensCoreRecorrentes,
    imgqMedio: imgqs.length ? imgqs.reduce((a, b) => a + b, 0) / imgqs.length : 0,
    imgqMin: imgqs.length ? Math.min(...imgqs) : 0,
    imgqMax: imgqs.length ? Math.max(...imgqs) : 0,
    imgqDesvio: desvio(imgqs),
    naMin,
    naMax,
    naSpread: naMax - naMin,
    naAlerta: naMax - naMin > 15,
    nTravadas: us.filter((u) => u.travado).length,
    distSelos,
  };
}

/**
 * Monta o Panorama da rede a partir das entradas por unidade. `nCiclos` é o total
 * de ciclos/avaliações consideradas (o hook passa `avaliacoes.length`); cada
 * unidade entra com o seu ciclo mais recente.
 */
export function panorama(entradas: readonly EntradaUnidade[], nCiclos: number): Panorama {
  const unidades = entradas
    .map(diagnosticar)
    .sort((a, b) => b.imgq - a.imgq || a.unidadeNome.localeCompare(b.unidadeNome));
  return {
    unidades,
    rede: cruzar(unidades, entradas),
    nUnidades: unidades.length,
    nCiclos,
  };
}
